import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { holdsOrgImage, RichText } from './rich-text';
import type { IPickedImage } from './rich-text-editor/toolbar';

/** A fresh lazy component per attempt: `React.lazy` keeps a rejected import, so a retry needs a
 *  new one. */
const lazyEditor = () =>
  React.lazy(() => import(/* webpackChunkName: "rich-text-editor" */ './rich-text-editor/editor'));

/** Why the editor is not drawn when its chunk failed to arrive. */
const EDITOR_UNAVAILABLE =
  'The editor could not be loaded — try again, or reload the page if this keeps happening.';

/** Why a value holding an org image cannot be edited yet (R13). */
export const ORG_IMAGE_READ_ONLY =
  'Holds an image kept in Salesforce — editable once the images are moved.';

/**
 * A rich-text value, edited — what an `InlineField`'s `rich_text` control draws (the advisor record
 * page's roadmap, K4; the F1 kit plan's R6, R8 and R13, Q1 ruled (b)).
 *
 * **The editor is a lazy chunk** (`rich-text-editor/`, Tiptap with its official extensions), so
 * Tiptap and ProseMirror reach only a browser that opens a rich-text field: an advisor has none to
 * edit, and the production build keeps every library in an initial-only vendor chunk for that
 * reason. While the chunk loads, a quiet line of text stands in — never the kit's skeleton, which
 * specs count across a page.
 *
 * **A value holding an org image is not opened** (R13): the editor would draw the image and so
 * load it from Salesforce, which `RichText` avoids everywhere else. It is drawn read-only with
 * `ORG_IMAGE_READ_ONLY` beside it; a field decides this before it offers a pencil, through
 * `holdsOrgImage`, and this arm is the backstop. Once survey-api's block 8 has inlined those images
 * as `data:`, the value opens like any other.
 *
 * **What the editor cannot hold is said, not here but above Save** — the shell owns Save, so the
 * sentence goes up through `onLosses` and comes back as the shell's `notice`. Save stays offered;
 * Cancel loses nothing.
 *
 * **A chunk that does not arrive is said, never a white screen.** After a deployment a tab still on
 * the old bundle asks for a chunk nginx answers with `index.html`, and a flaky network drops one;
 * `React.lazy` throws that from render, and with no boundary above it the whole page would unmount
 * — every other open field's draft with it. `ChunkBoundary` catches it here, says so, and offers
 * "Try again" over a fresh import.
 *
 * `value` is read when the editor opens and not after (see `editor.tsx`); `onChange` hands back the
 * markup, or `''` for an empty document.
 */
export function RichTextInput({
  value,
  onChange,
  label,
  disabled,
  onLosses,
  onPickImage,
  serverResolvedImages = false
}: {
  value: string;
  onChange: (html: string) => void;
  /** The editor's accessible name. */
  label: string;
  disabled?: boolean;
  onLosses?: (notice: Nullable<string>) => void;
  /** Where a library exists to draw a picture from — the content screens, and nothing else. */
  onPickImage?: () => Promise<Nullable<IPickedImage>>;
  /** The value is survey-api's, whose images it already judged — see `editor.tsx`. */
  serverResolvedImages?: boolean;
}) {
  const orgImage = React.useMemo(() => holdsOrgImage(value), [value]);
  const [attempt, setAttempt] = React.useState(0);
  const Editor = React.useMemo(lazyEditor, [attempt]);
  // A notice belongs to one opening: closing the editor takes it away, so the next opening (or a
  // chunk that fails to arrive) never shows the last one's.
  React.useEffect(() => () => onLosses?.(null), []);

  if (orgImage) {
    return (
      <div className="grid gap-1">
        <RichText html={value} serverResolvedImages={serverResolvedImages} />
        <p className="text-sm text-muted-foreground">{ORG_IMAGE_READ_ONLY}</p>
      </div>
    );
  }

  return (
    <ChunkBoundary key={attempt} onRetry={() => setAttempt((n) => n + 1)}>
      <React.Suspense
        fallback={<p className="py-2 text-sm text-muted-foreground">Loading the editor…</p>}
      >
        <Editor
          value={value}
          onChange={onChange}
          label={label}
          disabled={disabled}
          onLosses={onLosses}
          onPickImage={onPickImage}
          serverResolvedImages={serverResolvedImages}
        />
      </React.Suspense>
    </ChunkBoundary>
  );
}

class ChunkBoundary extends React.Component<
  { onRetry: () => void; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('[RichTextInput] the editor could not be loaded:', error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="grid justify-items-start gap-2 py-2 text-sm">
        <p>{EDITOR_UNAVAILABLE}</p>
        <Button type="button" variant="outline" size="sm" onClick={this.props.onRetry}>
          Try again
        </Button>
      </div>
    );
  }
}
